import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatearFecha } from '@/utils/dateUtils';
import { 
  ArrowLeft, 
  LogOut, 
  Bell, 
  CheckCircle, 
  AlertCircle, 
  FileText,
  Trash2,
  Eye,
  Upload
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BandejaEntrada({ user, onLogout }) {
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('pendientes'); // 'pendientes', 'resueltas', 'todas'
  const [deleteDialog, setDeleteDialog] = useState({ open: false, notificacionId: null });
  const [uploadDialog, setUploadDialog] = useState({ open: false, notificacion: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadNotificaciones();
  }, [filtro]);

  const loadNotificaciones = async () => {
    try {
      let url = `${API}/notificaciones`;
      if (filtro === 'pendientes') {
        url += '?solo_pendientes=true';
      } else if (filtro === 'resueltas') {
        // Cargar todas y filtrar las resueltas en el cliente
        const response = await axios.get(`${API}/notificaciones`);
        setNotificaciones(response.data.filter(n => n.resuelta));
        setLoading(false);
        return;
      }
      const response = await axios.get(url);
      
      // Filtrar notificaciones válidas (con trabajadores existentes)
      const notificacionesValidas = [];
      for (const notif of response.data) {
        if (notif.trabajador_id) {
          try {
            // Verificar que el trabajador existe
            await axios.get(`${API}/trabajadores/${notif.trabajador_id}`);
            notificacionesValidas.push(notif);
          } catch (err) {
            // Si el trabajador no existe, eliminar la notificación silenciosamente
            console.warn(`Notificación huérfana detectada (ID: ${notif.id}), eliminando...`);
            try {
              await axios.delete(`${API}/notificaciones/${notif.id}`);
            } catch (deleteErr) {
              console.error('Error eliminando notificación huérfana:', deleteErr);
            }
          }
        } else {
          notificacionesValidas.push(notif);
        }
      }
      
      setNotificaciones(notificacionesValidas);
    } catch (error) {
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLeida = async (id) => {
    try {
      await axios.put(`${API}/notificaciones/${id}/marcar-leida`);
      loadNotificaciones();
    } catch (error) {
      toast.error('Error al marcar notificación');
    }
  };

  const abrirDialogoSubida = (notificacion) => {
    setUploadDialog({ open: true, notificacion });
  };

  const handleUploadDocumento = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('tipo', uploadDialog.notificacion.documento_tipo);

    try {
      await axios.post(
        `${API}/trabajadores/${uploadDialog.notificacion.trabajador_id}/documentos`, 
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Documento subido exitosamente');
      setUploadDialog({ open: false, notificacion: null });
      setSelectedFile(null);
      loadNotificaciones();
    } catch (error) {
      toast.error('Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/notificaciones/${deleteDialog.notificacionId}`);
      toast.success('Notificación eliminada');
      loadNotificaciones();
    } catch (error) {
      toast.error('Error al eliminar notificación');
    } finally {
      setDeleteDialog({ open: false, notificacionId: null });
    }
  };

  const handleVerExpediente = async (trabajadorId) => {
    try {
      // Verificar que el trabajador existe antes de navegar
      await axios.get(`${API}/trabajadores/${trabajadorId}`);
      navigate(`/trabajadores/${trabajadorId}/expediente`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('El trabajador asociado a esta notificación ya no existe');
        // Recargar notificaciones para limpiar las huérfanas
        loadNotificaciones();
      } else {
        toast.error('Error al cargar el expediente del trabajador');
      }
    }
  };

  const getPrioridadBadge = (prioridad) => {
    const variants = {
      'alta': 'bg-red-100 text-red-800 border-red-200',
      'media': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'baja': 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return <Badge className={`${variants[prioridad]} border`}>{prioridad.toUpperCase()}</Badge>;
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'documento_pendiente':
        return <FileText className="w-5 h-5 text-orange-600" />;
      case 'seguimiento':
        return <Eye className="w-5 h-5 text-blue-600" />;
      case 'alerta':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const pendientesCount = notificaciones.filter(n => !n.resuelta).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Bell className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-emerald-900">Notificaciones</h2>
              <p className="text-sm text-gray-600">
                {pendientesCount} {pendientesCount === 1 ? 'pendiente' : 'pendientes'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant={filtro === 'pendientes' ? "default" : "outline"}
              onClick={() => setFiltro('pendientes')}
              className={filtro === 'pendientes' ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              data-testid="filter-pending"
            >
              Pendientes
            </Button>
            <Button
              variant={filtro === 'resueltas' ? "default" : "outline"}
              onClick={() => setFiltro('resueltas')}
              className={filtro === 'resueltas' ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              data-testid="filter-resolved"
            >
              Resueltas
            </Button>
            <Button
              variant={filtro === 'todas' ? "default" : "outline"}
              onClick={() => setFiltro('todas')}
              className={filtro === 'todas' ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              data-testid="filter-all"
            >
              Todas
            </Button>
          </div>
        </div>

        {loading ? (
          <Card className="border-0 shadow-md">
            <CardContent className="p-8 text-center text-gray-500">
              Cargando notificaciones...
            </CardContent>
          </Card>
        ) : notificaciones.length === 0 ? (
          <Card className="border-0 shadow-md">
            <CardContent className="p-8 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No hay notificaciones</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {notificaciones.map((notif, index) => (
              <motion.div
                key={notif.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                exit={{ opacity: 0, x: 20 }}
                layout
              >
              <Card 
                className={`border-0 shadow-md transition-all hover:shadow-lg ${
                  filtro === 'todas' && !notif.leida && !notif.resuelta ? 'bg-blue-50/50 border-l-4 border-l-orange-500' : 
                  filtro === 'todas' && notif.resuelta ? 'bg-green-50/30' : 
                  !notif.leida ? 'bg-blue-50/50' : 'bg-white'
                } ${notif.resuelta ? 'opacity-90' : ''}`}
                data-testid={`notificacion-${notif.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        {getTipoIcon(notif.tipo)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{notif.titulo}</h3>
                          {getPrioridadBadge(notif.prioridad)}
                          {notif.resuelta && (
                            <Badge className="bg-green-100 text-green-800 border-green-200 border">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Resuelta
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-700 mb-2">{notif.descripcion}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Trabajador: <strong>{notif.trabajador_nombre}</strong></span>
                          <span>•</span>
                          <span>Registrado por: <strong>{notif.created_by_name || notif.created_by}</strong></span>
                          <span>•</span>
                          <span>{formatearFecha(notif.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {notif.trabajador_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleVerExpediente(notif.trabajador_id)}
                          className="border-blue-200 hover:bg-blue-50 text-blue-600"
                          data-testid={`ver-expediente-${notif.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver Expediente
                        </Button>
                      )}
                      {!notif.resuelta && notif.tipo === 'documento_pendiente' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => abrirDialogoSubida(notif)}
                          className="border-green-200 hover:bg-green-50 text-green-600"
                          data-testid={`resolver-${notif.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Subir Documento
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteDialog({ open: true, notificacionId: notif.id })}
                        className="border-red-200 hover:bg-red-50 text-red-600"
                        data-testid={`eliminar-${notif.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialog.open} onOpenChange={(open) => setUploadDialog({ open, notificacion: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subir Documento</DialogTitle>
            <DialogDescription>
              {uploadDialog.notificacion && (
                <>Sube el documento: <strong>{uploadDialog.notificacion.documento_tipo}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Documento</Label>
              <Input
                value={uploadDialog.notificacion?.documento_tipo || ''}
                disabled
                className="bg-gray-100"
              />
            </div>
            <div className="space-y-2">
              <Label>Archivo *</Label>
              <Input
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                data-testid="upload-file-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setUploadDialog({ open: false, notificacion: null })}
              data-testid="cancel-upload"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleUploadDocumento}
              disabled={uploading || !selectedFile}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-upload"
            >
              {uploading ? 'Subiendo...' : 'Subir Documento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, notificacionId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar notificación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La notificación será eliminada permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
